import React from 'react';
import { Dispatch } from 'redux';

import axios from 'helpers/axios';
import config from 'helpers/config';
import Tenant from 'models/TenantModel';
import TenantSettings from 'models/TenantSettingsModel';
import Checkbox from 'components/common/Checkbox';
import FormModes from 'helpers/FormModes';
import {
    Save,
    Edit,
    X,
    Info,
    UserCheck,
    Mail,
    Phone,
    MapPin,
    Calendar,
    Users,
    CheckSquare,
    User,
    CircleGauge,
    Clock,
} from 'lucide-react';
import { FaCar } from 'react-icons/fa';
import BoilerplateTicketCard from 'components/common/BoilerplateTicketCard';
import { getTenantSettings } from 'actions/tenantActions';
import { connect } from 'react-redux';

interface Props {
    dispatch: Dispatch;
}

interface State {
    tenant: Tenant | null;
    companySettings: TenantSettings | null;
    formState: FormModes;
    beforeEdit: any;
}

class ROAgingRulesSettings extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            tenant: null,
            companySettings: null,
            formState: FormModes.Preview,
            beforeEdit: {},
        };

        this.handleChange = this.handleChange.bind(this);
    }

    componentDidMount() {
        this.getCompanySettings();
    }

    getCompanySettings() {
        axios.get(`${config.apiUrl}/tenant/settings`).then((res) =>
            this.setState({
                tenant: res.data,
                companySettings: {
                    ...res.data.tenantSettings,
                },
            }),
        );
    }

    save() {
        const { companySettings } = this.state;
        axios.put(`${config.apiUrl}/tenant/`, { ...companySettings }).then((res) => {
            this.setState({ tenant: res.data, companySettings, formState: FormModes.Preview, beforeEdit: {} });
            this.props?.dispatch<any>(getTenantSettings());
        });
    }

    handleChange(e: React.FormEvent<any>) {
        const { name, value, type } = e.currentTarget;
        const { companySettings } = this.state;
        if (companySettings) {
            // Convert numeric values for specific fields
            if (name === 'longRunningRoHighlightThresholdDays' || name === 'longRunningRoHighlightStyle') {
                (companySettings as any)[name] = Number(value);
            } else {
                (companySettings as any)[name] = value;
            }
            this.setState({ companySettings });
        }
    }
    render() {
        const { companySettings, formState } = this.state;

        return (
            <div className="flex flex-col min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                                <span>Settings</span>
                                <span>/</span>
                                <span className="text-blue-600 font-semibold">Long Running RO Tracking</span>
                            </nav>
                            <h2 className="text-3xl font-bold text-gray-900">Long Running RO Tracking</h2>
                            <p className="text-gray-600 mt-1">
                                Configure visual highlighting for repair orders that have been active in the system
                                longer than specified thresholds
                            </p>
                        </div>
                        <div className="flex items-center space-x-3">
                            {formState === FormModes.Preview && (
                                <button
                                    onClick={() => {
                                        this.setState({
                                            formState: FormModes.Edit,
                                            beforeEdit: { ...this.state.companySettings },
                                        });
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                                >
                                    <Edit className="h-4 w-4" />
                                    <span>Edit Settings</span>
                                </button>
                            )}

                            {formState === FormModes.Edit && (
                                <div className="flex space-x-3">
                                    <button
                                        onClick={() => {
                                            this.setState({
                                                companySettings: { ...this.state.beforeEdit },
                                                formState: FormModes.Preview,
                                                beforeEdit: {},
                                            });
                                        }}
                                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                                    >
                                        <X className="h-4 w-4" />
                                        <span>Cancel</span>
                                    </button>

                                    <button
                                        onClick={() => {
                                            this.save();
                                        }}
                                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                                    >
                                        <Save className="h-4 w-4" />
                                        <span>Save Changes</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex-1 p-8">
                    <div className="mb-8">
                        <div
                            className={`rounded-lg border divide-y divide-gray-200  bg-white ${
                                formState === FormModes.Edit ? ' border-blue-200' : ' border-gray-200'
                            }`}
                        >
                            <div
                                className={`flex items-center p-4 ${
                                    formState === FormModes.Edit ? 'bg-blue-50 ' : 'bg-white '
                                }`}
                            >
                                <div
                                    className={`p-2 rounded-lg ${
                                        formState === FormModes.Edit ? 'bg-blue-100' : 'bg-gray-100'
                                    }`}
                                >
                                    <Clock
                                        className={`h-5 w-5 ${
                                            formState === FormModes.Edit ? 'text-blue-600' : 'text-gray-600'
                                        }`}
                                    />
                                </div>
                                <div className="ml-4">
                                    <h3
                                        className={`text-lg font-semibold ${
                                            formState === FormModes.Edit ? 'text-blue-900' : 'text-gray-900'
                                        }`}
                                    >
                                        {formState === FormModes.Edit
                                            ? 'Editing Long Running RO Tracking Settings'
                                            : 'Long Running RO Tracking Settings'}
                                    </h3>
                                    <p
                                        className={`text-sm ${
                                            formState === FormModes.Edit ? 'text-blue-700' : 'text-gray-600'
                                        }`}
                                    >
                                        {formState === FormModes.Edit
                                            ? 'Make your changes below and click Save Changes to apply them.'
                                            : 'Currently configured rules for highlighting repair orders based on their age.'}
                                    </p>
                                </div>
                            </div>

                            <div className={` ${formState === FormModes.Preview ? 'opacity-75' : ''} pt-6 p-8`}>
                                <div className="flex space-x-4">
                                    <div>
                                        <Checkbox
                                            name={'isLongRunningRoHighlightEnabled'}
                                            value={companySettings?.isLongRunningRoHighlightEnabled}
                                            onChange={formState === FormModes.Preview ? undefined : this.handleChange}
                                            size={5}
                                            disabled={formState === FormModes.Preview}
                                        />
                                    </div>
                                    <div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                Enable Aging Highlights
                                            </h3>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-600 mt-1">
                                                Automatically highlight repair orders in the workflow board when they
                                                exceed the specified age threshold
                                            </p>
                                        </div>
                                        <div
                                            className={` ${
                                                companySettings?.isLongRunningRoHighlightEnabled ? '' : 'opacity-50'
                                            }`}
                                        >
                                            <div className="flex items-center space-x-1 pt-6">
                                                <p className="text-sm text-gray-600">Highlight tickets older than</p>
                                                <input
                                                    type="number"
                                                    className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                                                    defaultValue={7}
                                                    value={companySettings?.longRunningRoHighlightThresholdDays}
                                                    name="longRunningRoHighlightThresholdDays"
                                                    disabled={formState === FormModes.Preview}
                                                    onChange={
                                                        formState === FormModes.Preview ? undefined : this.handleChange
                                                    }
                                                />
                                                <p className="text-sm text-gray-600">days</p>
                                            </div>

                                            <div className="pt-6">
                                                <div className="mb-2">
                                                    <h3 className="text-md font-semibold text-gray-900">
                                                        Highlight Color
                                                    </h3>
                                                </div>
                                                <div className="flex items-center space-x-3">
                                                    <input
                                                        type="color"
                                                        name="longRunningRoHighlightColor"
                                                        onChange={this.handleChange}
                                                        value={
                                                            companySettings?.longRunningRoHighlightColor || '#000000'
                                                        }
                                                        disabled={formState === FormModes.Preview}
                                                        className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                                    />
                                                    <span className="text-sm text-gray-500">
                                                        {companySettings?.longRunningRoHighlightColor || '#000000'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="pt-6">
                                                <div className="mb-2">
                                                    <h3 className="text-md font-semibold text-gray-900">
                                                        Highlight Style
                                                    </h3>
                                                </div>
                                                <div className="space-y-3">
                                                    <label className="flex items-center">
                                                        <input
                                                            type="radio"
                                                            name="longRunningRoHighlightStyle"
                                                            value="0"
                                                            checked={companySettings?.longRunningRoHighlightStyle === 0}
                                                            onChange={this.handleChange}
                                                            disabled={formState === FormModes.Preview}
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                                        />
                                                        <span className="ml-2 text-sm text-gray-700">Border Only</span>
                                                    </label>
                                                    <label className="flex items-center">
                                                        <input
                                                            type="radio"
                                                            name="longRunningRoHighlightStyle"
                                                            value="1"
                                                            checked={companySettings?.longRunningRoHighlightStyle === 1}
                                                            onChange={this.handleChange}
                                                            disabled={formState === FormModes.Preview}
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                                        />
                                                        <span className="ml-2 text-sm text-gray-700">
                                                            Background Tint
                                                        </span>
                                                    </label>
                                                    <label className="flex items-center">
                                                        <input
                                                            type="radio"
                                                            name="longRunningRoHighlightStyle"
                                                            value="2"
                                                            checked={companySettings?.longRunningRoHighlightStyle === 2}
                                                            onChange={this.handleChange}
                                                            disabled={formState === FormModes.Preview}
                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                                        />
                                                        <span className="ml-2 text-sm text-gray-700">
                                                            Border + Background Tint
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={` ${formState === FormModes.Preview ? 'opacity-75' : ''} pt-6 p-8`}>
                                <div className="flex space-x-4">
                                    <div className="w-1/5">
                                        <h3 className="text-md font-semibold text-gray-900">Preview</h3>
                                        <BoilerplateTicketCard
                                            className="mt-4"
                                            longRunningRoHighlightColor={companySettings?.longRunningRoHighlightColor}
                                            longRunningRoHighlightStyle={companySettings?.longRunningRoHighlightStyle}
                                            showROAgeHighlight={companySettings?.isLongRunningRoHighlightEnabled}
                                            ageDaysToShow={
                                                companySettings?.longRunningRoHighlightThresholdDays
                                                    ? Number(companySettings?.longRunningRoHighlightThresholdDays) + 1
                                                    : 0
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Info Panel */}
                    <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex gap-4">
                            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-800">
                                <h4 className="font-semibold mb-3 text-lg">How Aging Rules Work</h4>
                                <ul className="space-y-2 text-blue-700">
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Aging is calculated from when the repair order was first created
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Highlights apply to all repair orders regardless of their current status
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Calendar days are used for aging calculations (includes weekends)
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Changes to these settings apply to all new and existing repair orders
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Highlighted tickets will update automatically as they age
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: any) => ({});

export default connect()(ROAgingRulesSettings);
